import React, { useEffect, useRef, useState } from 'react';

const useScrollAnimation = (threshold = 0.3) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const useCountUp = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
};

const StatCard = ({ value, prefix, suffix, label, sublabel, delay, started }) => {
  const count = useCountUp(value, 2000, started);
  return (
    <div
      className={`fade-up delay-${delay}`}
      style={{
        textAlign: 'center',
        padding: '2.5rem 1.5rem',
        position: 'relative',
      }}
    >
      {/* Top accent line */}
      <div style={{
        width: 40,
        height: 3,
        background: 'var(--red)',
        margin: '0 auto 1.5rem auto',
        borderRadius: 2,
      }} />

      <div style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 900,
        fontSize: 'clamp(3rem, 6vw, 4.5rem)',
        color: 'var(--text-primary)',
        lineHeight: 1,
        letterSpacing: '-0.02em',
      }}>
        {prefix}{count}{suffix}
      </div>

      <div style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 700,
        fontSize: '0.95rem',
        color: 'var(--text-primary)',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginTop: '0.75rem',
      }}>
        {label}
      </div>

      {sublabel && (
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.8rem',
          color: 'var(--gray)',
          marginTop: '0.3rem',
        }}>
          {sublabel}
        </div>
      )}
    </div>
  );
};

const Stats = () => {
  const ref = useScrollAnimation(0.3);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  const stats = [
    { value: 12, prefix: '+', suffix: '', label: 'Años de Experiencia', sublabel: 'En el mercado' },
    { value: 200, prefix: '+', suffix: '', label: 'Proyectos Completados', sublabel: 'Obra pública y privada' },
    { value: 8, suffix: '', label: 'Servicios Especializados', sublabel: 'Soluciones integrales' },
    { value: 100, suffix: '%', label: 'Compromiso', sublabel: 'Con cada cliente' },
  ];

  return (
    <section
      ref={ref}
      style={{
        position: 'relative',
        background: 'var(--bg-primary)',
        padding: '6rem 2rem',
        overflow: 'hidden',
        borderTop: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
      }}
    >
      {/* Red accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'var(--red)',
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: 1100,
        margin: '0 auto',
      }}>
        {/* Header */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label" style={{ justifyContent: 'center', display: 'flex' }}>
            Nuestros Números
          </div>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            color: 'var(--text-primary)',
            marginTop: '0.5rem',
          }}>
            Resultados que hablan por sí solos
          </h2>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 0,
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid var(--border-color)',
          background: 'var(--bg-card)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
        }} className="stats-grid">
          {stats.map((stat, i) => (
            <div
              key={i}
              style={{
                borderRight: i < stats.length - 1 ? '1px solid var(--border-color)' : 'none',
                transition: 'background 0.3s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(196,30,58,0.04)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <StatCard {...stat} delay={i + 1} started={started} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Stats;
